import "@progress/kendo-ui/js/kendo.datepicker";
import "@progress/kendo-ui/js/kendo.timepicker";
import * as ko from "knockout";
import * as _ from "lodash";
import * as moment from "moment";
import { AddFormDialog } from "../common/addFormDialog";
import * as AppointmentLocationLogMap from "../common/appointmentLocationLogMap";
import * as Constants from "../common/constants";
import * as FormDataEntry from "../common/formDataEntry";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as Util from "../common/util";
import * as AppointmentData from "../data/appointmentData";
import * as ReportData from "../data/reportData";
import * as TargetData from "../data/targetData";
import * as TemplateData from "../data/templateData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import { ICodeModel, IResponse } from "../interfaces/data";
import { ILanguage, ITranslatedText } from "../interfaces/translations";
import { ICustomerSettings } from "../interfaces/user";

// ==================================================================
// interfaces
// ==================================================================

// data needed to load this control with form data
interface IListObservable {
    ID: KnockoutObservable<number>;
    NavigationID: KnockoutObservable<number>;
    ListName: KnockoutObservable<string>;
    HasForm: KnockoutObservable<boolean>;
    HasAttributes: KnockoutObservable<boolean>;
    SortOrder: KnockoutObservable<number>;
    CanDelete: KnockoutObservable<boolean>;
    IsActive: KnockoutObservable<boolean>;

    /// lists that are children of this list.  These are lists that get
    /// created automatically when an appointment is created from
    /// this template.
    Children: KnockoutObservableArray<IListObservable>;

    /// if this list item is a form, contains the fields for the form
    /// required for save when HasForm is true.
    ListForm: AppointmentData.IAppointmentFormItem[];

    /// these are forms that can be added to this list.
    ListItems: KnockoutObservableArray<AppointmentData.IAppointmentListItem>;

    /// if HasForm is true, this will contain the ID of the
    /// form associated with the list item.
    TemplateFormID: KnockoutObservable<number>;

    // if true, then any child items are visible
    IsExpanded: KnockoutObservable<boolean>;

    // if true, then this item is being edited
    IsSelected: KnockoutObservable<boolean>;

    // if true, show action buttons for this item
    ShowActions: KnockoutObservable<boolean>;

    typeIsForm?: KnockoutComputed<boolean>;
    typeIsList?: KnockoutComputed<boolean>;
    numberOfLists?: KnockoutComputed<number>;
    rowStyle?: KnockoutComputed<string>;
}

interface ILinkedCustomerDocument {
    CustomerDocumentID: number;
    IsActive: boolean;
    Description: string;
    FileName: string;
}

// represents a top level navigation item displayed in the tree
interface INavigationObservable {
    ID: KnockoutObservable<number>;
    TemplateFormID: KnockoutObservable<number>;
    NavigationTypeID: KnockoutObservable<number>;
    NavigationName: KnockoutComputed<string>;
    NavigationNameDefault: KnockoutObservable<string>;
    NavigationNameTranslations: ITranslatedText[];
    NavigationIcon: KnockoutObservable<string>;
    SortOrder: KnockoutObservable<number>;

    // used when a navigation item goes directly to a form (TemplateFormID is not null).
    // contains all of the fields on that form.
    // only used when navigation type is 'Form'
    Forms: AppointmentData.IAppointmentFormItem[];

    // forms that can added under the navigation item
    ListItems: KnockoutObservableArray<AppointmentData.IAppointmentListItem>;

    // lists that are under the navigation item
    Lists: KnockoutObservableArray<IListObservable>;

    // computed functions
    typeIsForm?: KnockoutComputed<boolean>;
    typeIsList?: KnockoutComputed<boolean>;
    typeIsReport?: KnockoutComputed<boolean>;
    typeIsAppointmentDetail?: KnockoutComputed<boolean>;
    typeIsLocationLogMap?: KnockoutComputed<boolean>;

    numberOfLists?: KnockoutComputed<number>;
    rowStyle?: KnockoutComputed<string>;

    // if true, then any child items are visible
    IsExpanded: KnockoutObservable<boolean>;

    // if true, then this item is being edited
    IsSelected: KnockoutObservable<boolean>;

    // if true, show action buttons for this item
    ShowActions: KnockoutObservable<boolean>;
}

// view model for showing the appointment tree structure
interface IViewModelTree {
    Navigations: KnockoutObservableArray<INavigationObservable>;
    onEditAppointmentDetail: (navigationObservable: INavigationObservable) => void;
    onEditNavigation: (navigationObservable: INavigationObservable) => void;
    onShowNavigationReport: (navigationObservable: INavigationObservable) => void;
    onShowLocationLogMap: (navigationObservable: INavigationObservable) => void;
    onAddNavigationList: (navigationObservable: INavigationObservable) => void;
    onNavigationShowActions: (navigationObservable: INavigationObservable) => void;
    onNavigationHideActions: (navigationObservable: INavigationObservable) => void;
    onEditList: (listObservable: IListObservable) => void;
    onNavigationToggleExpanded: (navigationObservable: INavigationObservable) => void;
    onAddList: (listObservable: IListObservable) => void;
    onMoveListUp: (listObservable: IListObservable) => void;
    onMoveListDown: (listObservable: IListObservable) => void;
    onListToggleExpanded: (listObservable: IListObservable) => void;
    onListShowActions: (listObservable: IListObservable) => void;
    onListHideActions: (listObservable: IListObservable) => void;
}

// view model for editing appointment info like date, assigned user,etc.
interface IViewModelAppointmentDetail {
    AppointmentDate: KnockoutObservable<string>;
    AppointmentTime: KnockoutObservable<string>;
    UserID: KnockoutObservable<number>;
    StatusID: KnockoutObservable<number>;
    TargetDescription: KnockoutObservableArray<string>;
    TargetShortDescription: KnockoutObservable<string>;
    StatusValues: KnockoutObservableArray<ICodeModel<number>>;
    AppointmentUsers: KnockoutObservableArray<ICodeModel<number>>;
    AppointmentDateTimeInvalid: KnockoutObservable<boolean>;
    AppointmentExportUrl: KnockoutObservable<string>;
    AppointmentID: KnockoutObservable<number>;
    AppointmentTemplateID: KnockoutObservable<number>;
    AppointmentTemplateName: KnockoutObservable<string>;
    TargetID: KnockoutObservable<number>;
    TargetTemplateID: KnockoutObservable<number>;
    AppointmentLabel: KnockoutObservable<string>;
    AppointmentsLabel: KnockoutObservable<string>;
    LinkedCustomerDocuments: KnockoutObservableArray<ILinkedCustomerDocument>;
    CustomerDocumentDownloadUrl: string;
    DateFormat: string;
    TimeFormat: string;
    ReportEmailLogs: KnockoutObservableArray<AppointmentData.IReportEmailLog>;
    ReportEmailLogAttachmentDownloadURL: string;
    ReportID: KnockoutObservable<number | undefined>;
    ReportRecipients: KnockoutObservableArray<string>;
    RelatedAppointments: KnockoutObservableArray<AppointmentData.IRelatedAppointment>;
    MomentDateFormat: KnockoutObservable<string>;
    LanguageID: KnockoutObservable<number>;
    Languages: ILanguage[];
    viewReport: () => void;
    emailReport: () => void;
    copyTask: () => void;
    loadAppointment: (appointmentID: number) => void;
}

// view model for exception report
interface IViewModelExceptionReport {
    ExceptionNavigations: KnockoutObservableArray<AppointmentData.IAppointmentExceptionReportNavigation>;
    AppointmentLabel: KnockoutObservable<string>;
    QuestionLabel: KnockoutObservable<string>;
}

interface IAdditionalFormDialogViewModel {
    formItemIndex: KnockoutObservable<number>;
    field: KnockoutObservable<FormDataEntry.IFieldObservable | null>;
    hasExceptionForm: KnockoutObservable<boolean>;
    SaveProcessStarted: KnockoutObservable<boolean>;
    onSaveForm: () => void;
    onCancelForm: () => void;
}

// ==================================================================
// appointment Controller
// ==================================================================
export class TaskController implements IController {
    // -------------------------------------------------------------------------
    // class properties
    // -------------------------------------------------------------------------
    private container: JQuery;
    private viewModelTree: IViewModelTree;
    private additionalviewModel: IAdditionalFormDialogViewModel;
    private viewModelAppointmentDetail: IViewModelAppointmentDetail;
    private viewModelExceptionReport: IViewModelExceptionReport;
    private appointmentID: number;
    private navigationID: number;
    private listID: number | null;
    private templateFormID: number | null;
    private appointmentDetail: boolean;
    private formDetail: boolean;
    private formControl: FormDataEntry.FormDataEntryControl;
    private additionalFormControl: FormDataEntry.FormDataEntryControl;
    private locationLogMapControl: AppointmentLocationLogMap.AppointmentLocationLogMapControl;
    private addFormDialog: AddFormDialog;
    private customerSettings: ICustomerSettings[];
    // there are used to display additional navigation items
    // that are not part of the template.
    private NAVIGATIONTYPE_APPOINTMENTDETAIL = -1;
    private NAVIGATIONTYPE_LOCATIONLOGMAP = -2;

    // appointment controls
    private appointmentDateControl: JQuery;
    private appointmentTimeControl: JQuery;

    private fieldsOnLoad: string;

    // field to determine if validation should be skipped for form answers
    private skipValidation: boolean = false;

    // -------------------------------------------------------------------------
    // constructor
    // -------------------------------------------------------------------------
    constructor() {
        const me: TaskController = this;
        const app: IApplication = Global.app();
        this.customerSettings = app.userSession ? app.userSession.CustomerSettings : [];
        this.viewModelTree = this.createViewModelTree();
        this.viewModelExceptionReport = this.createViewModelExceptionReport();
        this.viewModelAppointmentDetail = this.createViewModelAppointmentDetail();
        this.additionalFormControl = new FormDataEntry.FormDataEntryControl({});
        this.formControl = new FormDataEntry.FormDataEntryControl({
            openAdditionalForm(formItemIndex: number, hasExceptionForm: boolean, templateFormID: number, field?: FormDataEntry.IFieldObservable): void {
                me.additionalviewModel.formItemIndex(formItemIndex);
                if (field) {
                    me.additionalviewModel.field(field);
                } else {
                    me.additionalviewModel.field(null);
                }
                me.additionalviewModel.hasExceptionForm(hasExceptionForm);
                me.openAdditionalForm(templateFormID);
            },

        });

        this.additionalviewModel = {
            formItemIndex: ko.observable(0),
            field: ko.observable(null),
            hasExceptionForm: ko.observable(false),
            SaveProcessStarted: ko.observable(false),

            onSaveForm(): void {
                this.SaveProcessStarted(true);
                me.save(true).always(() => {
                    this.SaveProcessStarted(false);
                });

            },
            onCancelForm(): void {
                me.container.find("div[data-id='additionalFormDialog']").modal("hide");

                if (me.additionalviewModel.hasExceptionForm()) {
                    const myformitems: FormDataEntry.IFormItemsMaster = me.formControl.getFormItems();
                    if (myformitems.FormItems[me.additionalviewModel.formItemIndex()].ControlTypeID === Constants.QuestionType.DROPDOWN) {
                        myformitems.FormItems[me.additionalviewModel.formItemIndex()].dropdownselected = null;
                    } else if (myformitems.FormItems[me.additionalviewModel.formItemIndex()].ControlTypeID === Constants.QuestionType.RADIOBUTTONLIST) {
                        myformitems.FormItems[me.additionalviewModel.formItemIndex()].selected = "";
                    } else if (myformitems.FormItems[me.additionalviewModel.formItemIndex()].ControlTypeID === Constants.QuestionType.CHECKBOXLIST) {
                        const selectedArray = myformitems.FormItems[me.additionalviewModel.formItemIndex()].selectedArray;
                        const field = me.additionalviewModel.field();
                        if (selectedArray && field) {
                            selectedArray.splice(selectedArray.indexOf(field.FieldValue()), 1);
                        }
                    }
                }
            },
        };

        this.addFormDialog = new AddFormDialog();

        this.locationLogMapControl = new AppointmentLocationLogMap.AppointmentLocationLogMapControl();
    }

    // -------------------------------------------------------------------------
    // iController implementation
    // -------------------------------------------------------------------------
    public save(isAddtionalForm: boolean = false): JQueryPromise<any> {

        const me: TaskController = this;
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any> = $.Deferred();

        // save appointment detail else save list form or navigation form
        if (this.appointmentDetail) {
            // save fields like appointment date, time, assigned user, etc.
            return this.saveAppointmentDetail();
        } else {
            if (this.isFormItemsValid(isAddtionalForm)) {
                waitContainer = UI.showWaitIndicator(me.container);
                if (isAddtionalForm) {
                    // get OrigAppointmentFormItemID to save additional form answers
                    const myformitems = me.formControl.getFormItems();
                    const originalFormItemID: number = myformitems.FormItems[me.additionalviewModel.formItemIndex()].ID;
                    AppointmentData.SaveAppointmentAdditionalForm(ko.toJS(me.additionalFormControl.getFormItemsForSave()),
                        this.navigationID, this.listID, originalFormItemID)
                        .done((response: IResponse<AppointmentData.ISaveAppointmentResponse>) => {
                            if (response.Success) {
                                me.container.find("div[data-id='additionalFormDialog']").modal("hide");
                                UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);

                                deferred.resolve();
                            } else {
                                UI.showErrorMessage(response.ErrorMessages);
                                deferred.reject();
                            }
                        })
                        .fail(() => {
                            deferred.reject();
                        })
                        .always(() => {
                            UI.hideWaitIndicator(me.container, waitContainer);
                        });

                } else {
                    AppointmentData.SaveAppointmentForm(ko.toJS(me.formControl.getFormItemsForSave()), this.navigationID, this.skipValidation, this.listID)
                        .done((response: IResponse<AppointmentData.ISaveAppointmentResponse>): void => {
                            if (response.Success) {
                                UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                                me.loadFormItemsByTemplateFormID(me.templateFormID, me.appointmentID).done(() => {
                                    deferred.resolve();
                                })
                                .fail(() => {
                                    deferred.reject();
                                });
                            } else {
                                UI.showErrorMessage(response.ErrorMessages);
                                deferred.reject();
                            }
                        })
                        .fail(() => {
                            deferred.reject();
                        })
                        .always(() => {
                            UI.hideWaitIndicator(me.container, waitContainer);
                        });
                }
            } else {
                // not all valid fields are entered
                deferred.reject();
            }
        }
        return deferred.promise();
    }

    public render(container: JQuery): JQueryPromise<any> {
        const me: TaskController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const appointment: IViewModelAppointmentDetail = this.viewModelAppointmentDetail;

        me.container = container;
        me.formControl.render(container.find("div[data-id='formContainer']").find(".tree-detail-container")).done(() => {
            me.additionalFormControl.render(container.find("div[data-id='additionalFormContainer']")).done(() => {
                me.locationLogMapControl.render(container.find("div[data-id='locationLogMapContainer']")).done(() => {
                    ko.applyBindings(me.viewModelTree, container.find("div[data-id='treeContainer']")[0]);
                    ko.applyBindings(me.viewModelAppointmentDetail, container.find("div[data-id='appointmentDetailContainer']")[0]);
                    ko.applyBindings(me.additionalviewModel, container.find("div[data-id='additionalFormButtons']")[0]);
                    ko.applyBindings(me.viewModelExceptionReport, container.find("div[data-id='exceptionReportContainer']")[0]);

                    me.addFormDialog.render(container);

                    me.appointmentDateControl = me.container.find("input[data-id='appointmentDate']");
                    me.appointmentTimeControl = me.container.find("input[data-id='appointmentTime']");

                    me.appointmentDateControl.kendoDatePicker({ format: UI.getKendoDateFormat(appointment.DateFormat) });
                    me.appointmentTimeControl.kendoTimePicker({ format: UI.getKendoTimeFormat(appointment.TimeFormat), interval: 15 });

                    deferred.resolve();
                });
            });
        });

        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        if (params != null && params.length === 1) {
            this.appointmentID = params[0];
        } else {
            this.appointmentID = 0;
        }
        return this.loadRecord(this.appointmentID);
    }

    public haveFieldsChanged(): boolean {
        const me: TaskController = this;
        return me.detailsChanged();
    }

    public height(height: number): void {
        const detailHeight: number = height - 60;
        if (detailHeight > 0) {
            // set the min height of the detail area, so the containing box always extends to the bottom of the visible page
            this.container.find("div[data-id='formContainer']").find(".tree-detail-container").css("min-height", detailHeight);
            this.container.find("div[data-id='additionalFormContainer']").find(".tree-detail-container").css("min-height", detailHeight);
            this.container.find("div[data-id='appointmentDetailContainer']").find(".tree-detail-container").css("min-height", detailHeight);
        }
    }

    public appRouterID(params: any[]): string {
        let routerID: string = "Appointment";
        if (params != null && params.length === 1) {
            routerID += params[0];
        }
        return routerID;
    }

    public title(): string {
        return Constants.LABEL_APPOINTMENT;
    }

    public subTitle(): string {
        let subTitle: string = "";
        if (this.appointmentID == null) {
            subTitle = "New";
        } else {
            subTitle = this.viewModelAppointmentDetail.TargetShortDescription();
        }
        return subTitle;
    }

    // -------------------------------------------------------------------------
    // create view model methods
    // -------------------------------------------------------------------------
    private createViewModelExceptionReport(): IViewModelExceptionReport {
        return {
            ExceptionNavigations: ko.observableArray<AppointmentData.IAppointmentExceptionReportNavigation>(),
            AppointmentLabel: ko.observable(Constants.LABEL_APPOINTMENT),
            QuestionLabel: ko.observable(Constants.LABEL_QUESTION),
        };
    }

    private createViewModelTree(): IViewModelTree {
        const me: TaskController = this;

        return {
            Navigations: ko.observableArray<INavigationObservable>(),
            onEditAppointmentDetail(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                    me.editAppointmentDetail(navigationObservable);
                    // Need to reload the appointment because form changes may impact the detail screen (ex the Email Report btn)
                    // TODO: If this proves to be slow we may need to make a smaller call for just the report recipients
                    me.loadRecord(me.appointmentID);
                });
            },
            onEditNavigation(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                    me.editNavigation(navigationObservable);
                });
            },
            onShowNavigationReport(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                    me.showNavigationReport(navigationObservable);
                });
            },
            onShowLocationLogMap(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                    me.showLocationLogMap(navigationObservable);
                });
            },
            onAddNavigationList(navigationObservable: INavigationObservable): void {
                me.confirmDetailChange(() => {
                     me.addFormDialog.show(navigationObservable.ListItems(),
                        (listName: string, templateFormID: number, ID: number) => {
                            me.createNewForm(ID, listName, templateFormID, navigationObservable.ID())
                                .done((listObservable: IListObservable) => {
                                        navigationObservable.Lists.push(listObservable);
                                        me.editList(listObservable);
                                });
                            });
                });
            },
            onNavigationShowActions(navigationObservable: INavigationObservable): void {
                navigationObservable.ShowActions(true);
            },
            onNavigationHideActions(navigationObservable: INavigationObservable): void {
                navigationObservable.ShowActions(false);
            },
            onEditList(listObservable: IListObservable): void {
                me.confirmDetailChange(() => {
                    me.editList(listObservable);
                });
            },
            onNavigationToggleExpanded(navigationObservable: INavigationObservable): void {
                navigationObservable.IsExpanded(!navigationObservable.IsExpanded());
            },
            onAddList(listObservable: IListObservable): void {
                me.addFormDialog.show(listObservable.ListItems(), (listName: string, templateFormID: number, ID: number) => {
                    me.createNewForm(ID, listName, templateFormID, listObservable.NavigationID())
                        .done((newListObservable: IListObservable) => {
                            listObservable.Children.push(newListObservable);
                        });
                });
            },
            onMoveListUp(listObservable: IListObservable): void {
                me.moveListItem(listObservable, true);
            },
            onMoveListDown(listObservable: IListObservable): void {
                me.moveListItem(listObservable, false);
            },
            onListToggleExpanded(listObservable: IListObservable): void {
                listObservable.IsExpanded(!listObservable.IsExpanded());
            },
            onListShowActions(listObservable: IListObservable): void {
                listObservable.ShowActions(true);
            },
            onListHideActions(listObservable: IListObservable): void {
                listObservable.ShowActions(false);
            },
        };
    }

    private createViewModelAppointmentDetail(): IViewModelAppointmentDetail {
        const app: IApplication = Global.app();
        const me = this;

        let languages: ILanguage[] = [];
        if (app.userSession && app.userSession.Languages) {
            languages = app.userSession.Languages;
        }

        const viewModel: IViewModelAppointmentDetail = {
            AppointmentDate: ko.observable<string>(),
            AppointmentTime: ko.observable<string>(),
            UserID: ko.observable<number>(),
            StatusID: ko.observable<number>(),
            StatusValues: ko.observableArray<ICodeModel<number>>(),
            AppointmentUsers: ko.observableArray<ICodeModel<number>>(),
            TargetDescription: ko.observableArray<string>(),
            TargetShortDescription: ko.observable<string>(""),
            AppointmentDateTimeInvalid: ko.observable<boolean>(false),
            AppointmentExportUrl: ko.observable<string>(),
            AppointmentID: ko.observable(),
            AppointmentTemplateName: ko.observable(),
            AppointmentTemplateID: ko.observable(),
            TargetID: ko.observable(),
            TargetTemplateID: ko.observable(),
            AppointmentLabel: ko.observable(Constants.LABEL_APPOINTMENT),
            AppointmentsLabel: ko.observable(Constants.LABEL_APPOINTMENTS),
            LinkedCustomerDocuments: ko.observableArray(),
            CustomerDocumentDownloadUrl: app.webServiceBaseURL + "/api/DocumentLibrary/Download/",
            DateFormat: UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT),
            TimeFormat: UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_TIMEFORMAT, Constants.DEFAULT_TIME_FORMAT),
            ReportEmailLogs: ko.observableArray(),
            ReportEmailLogAttachmentDownloadURL: app.webServiceBaseURL + "/api/Report/ReportEmailLogAttachment/",
            ReportID: ko.observable(),
            ReportRecipients: ko.observableArray(),
            RelatedAppointments: ko.observableArray(),
            MomentDateFormat: ko.observable(),
            LanguageID: ko.observable(0),
            Languages: languages,
            viewReport: () => {
                app.router().navigate(`/ViewReport/${viewModel.ReportID()}?appointmentID=${viewModel.AppointmentID()}`, true);
            },
            emailReport: () => {

                const recipients = me.viewModelAppointmentDetail.ReportRecipients().join(", ");

                UI.showConfirmDialog(`Are you sure you want to email this report?  \n\nRecipients: ${recipients}`,
                    () => {
                        const waitcontainer = UI.showWaitIndicator(this.container);
                        ReportData.EmailReportToDefaultRecipients(me.viewModelAppointmentDetail.AppointmentID())
                            .then((response) => {
                                if (response.Success) {
                                    UI.showNotificationMessage("Report Emailed", UI.messageTypeValues.success);
                                    me.loadRecord(me.viewModelAppointmentDetail.AppointmentID());
                                } else {
                                    UI.showErrorMessage(response.ErrorMessages);
                                }
                            })
                            .always(() => {
                                UI.hideWaitIndicator(me.container, waitcontainer);
                            });
                    });
            },
            copyTask: () => {
                UI.showConfirmDialog(`Are you sure you want to copy this ${Constants.LABEL_APPOINTMENT}?`, () => {

                    const waitcontainer = UI.showWaitIndicator(this.container);

                    AppointmentData.CloneAppointment(viewModel.AppointmentID()).then((response) => {
                        if (response.Success) {
                            UI.showNotificationMessage(`${Constants.LABEL_APPOINTMENT} copied.`, UI.messageTypeValues.success);
                            app.pageManager().gotoTaskPage(response.Payload, false);

                        } else {
                            UI.showErrorMessage(response.ErrorMessages);
                        }
                    })
                        .always(() => {
                            UI.hideWaitIndicator(this.container, waitcontainer);
                        });
                });
            },
            loadAppointment: (appointmentID: number) => {
                app.pageManager().gotoTaskPage(appointmentID, false);
            },
        };

        return viewModel;
    }

    // -------------------------------------------------------------------------
    // load/update view model methods
    // -------------------------------------------------------------------------
    private loadAdditionalFormItemsByTemplateFormID(templateFormID: number, appointmentID: number,
        origFormItemID?: number): JQueryPromise<any> {
        const me: TaskController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        AppointmentData.GetAdditionalFormItemsByTemplateFormID(templateFormID, appointmentID,
            this.navigationID, origFormItemID, this.listID)
            .done((response: IResponse<AppointmentData.IGetAdditionalFormItemResponse>) => {
                if (response.Success) {
                    me.updateAdditionalFormItems(response.Payload.AppointmentFormItem);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private loadFormItemsByTemplateFormID(templateFormID: number | null, appointmentID: number,
        origFormItemID?: number): JQueryPromise<any> {
        const me: TaskController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        AppointmentData.GetFormItemsByTemplateFormID(templateFormID, appointmentID, this.navigationID, origFormItemID, this.listID)
            .done((response: IResponse<AppointmentData.IGetFormItemResponse>) => {
                if (response.Success) {
                    me.updateFormItems(response.Payload.AppointmentFormItem);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private updateAdditionalFormItems(appointmentFormItems: AppointmentData.IAppointmentFormItem[]): void {
        const formItems: AppointmentData.IAppointmentFormItem[] = appointmentFormItems.map((formItem) => {
            const formItemCopy: AppointmentData.IAppointmentFormItem = {
                ID: formItem.ID,
                TemplateFormItemID: formItem.TemplateFormItemID,
                FormAnswer: formItem.FormAnswer,
                Comments: formItem.Comments,
                Documents: formItem.Documents,
                AdditionalFormItems: formItem.AdditionalFormItems,
                ControlTypeID: formItem.ControlTypeID,
                AppointmentID: formItem.AppointmentID,
                FormText: formItem.FormText,
                FormTextTranslations: formItem.FormTextTranslations,
                SortOrder: formItem.SortOrder,
                IsRequired: formItem.IsRequired,
                IsReadOnly: formItem.IsReadOnly,
                IsVisible: formItem.IsVisible,
                Fields: formItem.Fields,
                AppointmentControlTypeAttribute: formItem.AppointmentControlTypeAttribute,
                AppointmentFormItemDocumentTypesAllowed: formItem.AppointmentFormItemDocumentTypesAllowed,
                TemplatePhraseID: formItem.TemplatePhraseID,
                TemplatePhrases: formItem.TemplatePhrases,
                AdditionalTemplateFormID: formItem.AdditionalTemplateFormID,
                IsAdditionalForm: true,
                AppointmentFormItemLinkedCustomerDocuments: formItem.AppointmentFormItemLinkedCustomerDocuments,
                MultipleAdditionalForms: formItem.MultipleAdditionalForms,
                Targets: formItem.Targets,
            };
            return formItemCopy;
        });

        const loadParams: FormDataEntry.ILoadParams = {
            FormItems: formItems,
            LanguageID: this.viewModelAppointmentDetail.LanguageID(),
        };

        this.additionalFormControl.load(loadParams);
        this.additionalFormControl.show();
        this.showAdditionalFormDialog();
    }

    private updateExceptionReport(items: AppointmentData.IAppointmentExceptionReport): void {
        this.viewModelExceptionReport.ExceptionNavigations(items.ExceptionReportNavigations);
    }

    private updateFormItems(appointmentFormItems: AppointmentData.IAppointmentFormItem[]): void {
        const formItems: AppointmentData.IAppointmentFormItem[] = appointmentFormItems.map((formItem: AppointmentData.IAppointmentFormItem) => {
            const formItemCopy: AppointmentData.IAppointmentFormItem = {
                ID: formItem.ID,
                TemplateFormItemID: formItem.TemplateFormItemID,
                FormAnswer: formItem.FormAnswer,
                Comments: formItem.Comments,
                Documents: formItem.Documents,
                AdditionalFormItems: formItem.AdditionalFormItems,
                ControlTypeID: formItem.ControlTypeID,
                AppointmentID: formItem.AppointmentID,
                FormText: formItem.FormText,
                FormTextTranslations: formItem.FormTextTranslations,
                SortOrder: formItem.SortOrder,
                IsRequired: formItem.IsRequired,
                IsReadOnly: formItem.IsReadOnly,
                IsVisible: formItem.IsVisible,
                Fields: formItem.Fields,
                AppointmentControlTypeAttribute: formItem.AppointmentControlTypeAttribute,
                AppointmentFormItemDocumentTypesAllowed: formItem.AppointmentFormItemDocumentTypesAllowed,
                TemplatePhraseID: formItem.TemplatePhraseID,
                TemplatePhrases: formItem.TemplatePhrases,
                AdditionalTemplateFormID: formItem.AdditionalTemplateFormID,
                AppointmentFormItemLinkedCustomerDocuments: formItem.AppointmentFormItemLinkedCustomerDocuments,
                MultipleAdditionalForms: formItem.MultipleAdditionalForms,
                Targets: formItem.Targets,
            };

            return formItemCopy;
        });

        const loadParams: FormDataEntry.ILoadParams = {
            FormItems: formItems,
            LanguageID: this.viewModelAppointmentDetail.LanguageID(),
        };

        this.formControl.load(loadParams);
    }

    private loadRecord(id: number): JQueryPromise<any> {
        const me: TaskController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);
        AppointmentData.GetMaster(id)
            .done((response: IResponse<AppointmentData.IAppointmentMaster>) => {
                if (response.Success) {
                    me.updateViewModel(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
                deferred.resolve();
            });

        return deferred.promise();
    }

    private updateViewModel(master: AppointmentData.IAppointmentMaster): void {
        this.updateViewModelTree(master.Appointment);
        this.updateViewModelAppointmentDetail(master);

        // show the 'appointment' section by default
        this.editAppointmentDetail(this.viewModelTree.Navigations()[0]);
    }

    private updateViewModelTree(appointment: AppointmentData.IAppointment): void {
        const me: TaskController = this;

        this.viewModelTree.Navigations.removeAll();
        me.viewModelTree.Navigations.push(this.createNavigationObservable({
            ID: 0,
            Lists: [],
            Forms: [],
            NavigationTypeID: this.NAVIGATIONTYPE_APPOINTMENTDETAIL,
            TemplateFormID: null,
            NavigationName: Constants.LABEL_APPOINTMENT,
            NavigationNameTranslations: [],
            NavigationIcon: null,
            SortOrder: 0,
            ListItems: [],
        }));

        appointment.Navigations.forEach((navigation: AppointmentData.IAppointmentNavigation) => {
            me.viewModelTree.Navigations.push(me.createNavigationObservable(navigation));
        });

        me.viewModelTree.Navigations.push(this.createNavigationObservable({
            ID: 0,
            Lists: [],
            Forms: [],
            NavigationTypeID: this.NAVIGATIONTYPE_LOCATIONLOGMAP,
            TemplateFormID: null,
            NavigationName: "Map",
            NavigationNameTranslations: [],
            NavigationIcon: null,
            SortOrder: 0,
            ListItems: [],
        }));
    }

    private createNavigationObservable(navigation: AppointmentData.IAppointmentNavigation): INavigationObservable {
        const me: TaskController = this;
        const navigationObservable: INavigationObservable = {
            ID: ko.observable<number>(navigation.ID),
            TemplateFormID: ko.observable<number>(navigation.TemplateFormID),
            NavigationTypeID: ko.observable<number>(navigation.NavigationTypeID),
            NavigationName: ko.pureComputed(() => {
                // Find the translation for the currently selected language
                if (navigationObservable.NavigationNameTranslations) {
                    const translations = navigationObservable.NavigationNameTranslations
                            .filter((navigationNameTranslations) => navigationNameTranslations.LanguageID === me.viewModelAppointmentDetail.LanguageID());
                    if (translations && translations.length > 0 && translations[0].TranslatedText) {
                        return translations[0].TranslatedText;
                    } else {
                        return navigationObservable.NavigationNameDefault();
                    }
                } else {
                    return navigationObservable.NavigationNameDefault();
                }
            }),
            NavigationNameDefault: ko.observable<string>(navigation.NavigationName),
            NavigationNameTranslations: navigation.NavigationNameTranslations,
            NavigationIcon: ko.observable<string>(navigation.NavigationIcon),
            SortOrder: ko.observable<number>(navigation.SortOrder),
            IsExpanded: ko.observable<boolean>(true),
            ListItems: ko.observableArray<AppointmentData.IAppointmentListItem>(navigation.ListItems),
            Lists: ko.observableArray<IListObservable>(this.createListObservables(navigation.Lists, navigation.ID)),
            Forms: navigation.Forms,
            IsSelected: ko.observable<boolean>(false),
            ShowActions: ko.observable<boolean>(false),
        };

        navigationObservable.typeIsForm = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_FORM;
        });

        navigationObservable.typeIsList = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_LIST;
        });

        navigationObservable.typeIsReport = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === TemplateData.NAVIGATIONTYPE_REPORT;
        });

        navigationObservable.typeIsAppointmentDetail = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === me.NAVIGATIONTYPE_APPOINTMENTDETAIL;
        });
        navigationObservable.typeIsLocationLogMap = ko.pureComputed((): boolean => {
            return navigationObservable.NavigationTypeID() === me.NAVIGATIONTYPE_LOCATIONLOGMAP;
        });

        navigationObservable.numberOfLists = ko.pureComputed((): number => {
            return navigationObservable.Lists().length;
        });

        navigationObservable.rowStyle = ko.pureComputed((): string => {
            if (navigationObservable.IsSelected()) {
                return "tree-item tree-item-selected";
            } else {
                return "tree-item";
            }
        });

        return navigationObservable;
    }

    private updateViewModelAppointmentDetail(appointmentMaster: AppointmentData.IAppointmentMaster): void {
        const app: IApplication = Global.app();
        const me: TaskController = this;
        const appointment: IViewModelAppointmentDetail = this.viewModelAppointmentDetail;
        const momentDate: string = UI.getMomentDateFormat(appointment.DateFormat);
        const momentTime: string = UI.getMomentTimeFormat(appointment.TimeFormat);
        // drop down lists must be populated before setting the field values
        appointment.StatusValues(appointmentMaster.StatusValues);
        appointment.AppointmentUsers(appointmentMaster.AppointmentUsers);

        // set field values
        if (appointmentMaster.Appointment.AppointmentDate != null) {

            appointment.AppointmentDate(moment(appointmentMaster.Appointment.AppointmentDate).format(momentDate));
            appointment.AppointmentTime(moment(appointmentMaster.Appointment.AppointmentDate).format(momentTime));

            // note that updating the view model does not update the value in the date and time controls.  So also
            // manually push the date and time values into the control
            me.appointmentDateControl.data("kendoDatePicker").value(appointment.AppointmentDate());
            me.appointmentTimeControl.data("kendoTimePicker").value(appointment.AppointmentTime());
        }

        const docList: ILinkedCustomerDocument[] = [];
        for (const doc of appointmentMaster.Appointment.LinkedCustomerDocuments) {
            docList.push({
                CustomerDocumentID: doc.CustomerDocumentID,
                Description: doc.Description,
                FileName: doc.FileName,
                IsActive: doc.IsActive,
            });
        }

        for (const doc of appointmentMaster.Appointment.Target.LinkedCustomerDocuments) {

            docList.push({
                CustomerDocumentID: doc.CustomerDocumentID,
                Description: doc.Description,
                FileName: doc.FileName,
                IsActive: doc.IsActive,
            });
        }

        appointment.LinkedCustomerDocuments(_.uniqWith(docList, (a, b) => a.CustomerDocumentID === b.CustomerDocumentID));

        appointment.UserID(appointmentMaster.Appointment.UserID);
        appointment.StatusID(appointmentMaster.Appointment.StatusID);
        appointment.AppointmentExportUrl(`${app.webServiceBaseURL}/api/appointment/export/${me.appointmentID}`);

        for (const log of appointmentMaster.ReportEmailLogs) {
            log.DateTimeCreated = moment(log.DateTimeCreated)
                .format(UI.GetCustomerSetting(this.customerSettings, Constants.SETTING_KEY_DATEFORMAT, Constants.DEFAULT_DATE_FORMAT));
        }

        appointment.ReportEmailLogs(appointmentMaster.ReportEmailLogs);
        appointment.TargetDescription.removeAll();
        this.getTargetDescription(appointmentMaster.Appointment.Target).forEach((value: string) => {
            appointment.TargetDescription.push(value);
        });
        appointment.TargetShortDescription(this.getTargetShortDescription(appointmentMaster.Appointment.Target));

        appointment.AppointmentID(appointmentMaster.Appointment.ID);
        appointment.AppointmentTemplateID(appointmentMaster.Appointment.TemplateID);
        appointment.AppointmentTemplateName(appointmentMaster.Appointment.TemplateName);
        appointment.TargetID(appointmentMaster.Appointment.TargetID);
        appointment.TargetTemplateID(appointmentMaster.Appointment.Target.TargetTemplateID);
        appointment.ReportID(appointmentMaster.Appointment.ReportID);
        appointment.ReportRecipients(appointmentMaster.Appointment.ReportRecipients);
        appointment.RelatedAppointments(appointmentMaster.RelatedAppointments);
        appointment.MomentDateFormat(momentDate);

        appointment.LanguageID(appointmentMaster.Appointment.LanguageID);

        this.fieldsOnLoad = ko.toJSON(this.viewModelAppointmentDetail);
    }

    // returns a read-only description of the target as an array of strings
    private getTargetDescription(target: AppointmentData.IAppointmentTarget): string[] {
        const description: string[] = [];
        let cityStateZip: string = "";

        switch (target.TargetTemplateTypeID) {
            case TargetData.TARGETEMPLTETYPEID_LOCATION:
                if (!Util.isNullOrEmpty(target.Address)) { description.push(target.Address); }
                if (!Util.isNullOrEmpty(target.Address2)) { description.push(target.Address2); }

                if (!Util.isNullOrEmpty(target.City)) { cityStateZip += target.City + ", "; }
                if (!Util.isNullOrEmpty(target.State)) { cityStateZip += target.State + " "; }
                if (!Util.isNullOrEmpty(target.Zip)) { cityStateZip += target.Zip; }
                if (!Util.isNullOrEmpty(cityStateZip)) { description.push(cityStateZip); }

                if (!Util.isNullOrEmpty(target.Phone)) { description.push(target.Phone); }

                break;

            case TargetData.TARGETEMPLTETYPEID_CUSTOM:
                for (const formItem of target.FormItems) {
                    if (!Util.isNullOrEmpty(formItem.FormText)) {
                        const formItemValue = formItem.Value === null ? "" : UI.formatValueFromControlType(formItem.Value, formItem.ControlTypeID);
                        description.push(`${formItem.FormText} : ${formItemValue}`);
                    }
                }
                break;
        }

        return description;
    }

    private getTargetShortDescription(target: AppointmentData.IAppointmentTarget): string {
        let description: string = "";

        switch (target.TargetTemplateTypeID) {
            case TargetData.TARGETEMPLTETYPEID_LOCATION:
                description = target.Address;
                break;

            case TargetData.TARGETEMPLTETYPEID_CUSTOM:
                if (target.FormItems.length > 0) {
                    description = target.FormItems[0].Value;
                }
                break;
        }

        return description;
    }

    private createListObservables(appointmentLists: AppointmentData.IAppointmentList[], navigationID: number): IListObservable[] {
        const me: TaskController = this;
        const listObservables: IListObservable[] = [];

        appointmentLists.forEach((appointmentList: AppointmentData.IAppointmentList) => {
            listObservables.push(me.createListObservable(appointmentList, navigationID));
        });

        return listObservables;
    }

    private createListObservable(appointmentList: AppointmentData.IAppointmentList, navigationID: number): IListObservable {
        const listObservable: IListObservable = {
            ID: ko.observable<number>(appointmentList.ID),
            NavigationID: ko.observable<number>(navigationID),
            ListName: ko.observable<string>(appointmentList.ListName),
            HasForm: ko.observable<boolean>(appointmentList.HasForm),
            HasAttributes: ko.observable<boolean>(appointmentList.HasAttributes),
            SortOrder: ko.observable<number>(appointmentList.SortOrder),
            CanDelete: ko.observable<boolean>(appointmentList.CanDelete),
            IsActive: ko.observable<boolean>(appointmentList.IsActive),
            Children: ko.observableArray<IListObservable>(this.createListObservables(appointmentList.Children, navigationID)),
            ListItems: ko.observableArray<AppointmentData.IAppointmentListItem>(appointmentList.ListItems),
            ListForm: appointmentList.ListForm,
            TemplateFormID: ko.observable<number>(appointmentList.TemplateFormID),
            IsExpanded: ko.observable<boolean>(true),
            IsSelected: ko.observable<boolean>(false),
            ShowActions: ko.observable<boolean>(false),
        };

        listObservable.typeIsForm = ko.pureComputed((): boolean => {
            return listObservable.HasForm();
        });

        listObservable.typeIsList = ko.pureComputed((): boolean => {
            return !listObservable.HasForm();
        });

        listObservable.numberOfLists = ko.pureComputed((): number => {
            return listObservable.Children().length;
        });

        listObservable.rowStyle = ko.pureComputed((): string => {
            if (listObservable.IsSelected()) {
                return "tree-item tree-item-selected";
            } else {
                return "tree-item";
            }
        });

        return listObservable;
    }

    // -------------------------------------------------------------------------
    // appointment detail edit functions
    // -------------------------------------------------------------------------

    private editAppointmentDetail(navigationObservable: INavigationObservable): void {
        // show the area that has the appointment fields, like date, assigned user, etc.
        this.hideExceptionReportContainer();
        this.hideFormContainer();
        this.showAppointmentDetailContainer();
        this.hideLocationLogMapContainer();
        this.appointmentDetail = true;
        this.formDetail = false;
        this.listID = null;
        this.templateFormID = null;

        // update the UI
        this.unselectAllNavigation();
        navigationObservable.IsSelected(true);
    }

    // -------------------------------------------------------------------------
    // location log map
    // -------------------------------------------------------------------------

    private showLocationLogMap(navigationObservable: INavigationObservable): void {
        // shows a map that shows the points logged during the inspection
        this.hideAppointmentDetailContainer();
        this.hideExceptionReportContainer();
        this.hideFormContainer();

        this.showLocationLogMapContainer();
        this.locationLogMapControl.show();

        this.locationLogMapControl.load(this.appointmentID);

        this.appointmentDetail = false;
        this.formDetail = false;

        this.listID = null;
        this.templateFormID = null;

        // update the UI
        this.unselectAllNavigation();
        navigationObservable.IsSelected(true);
    }

    // -------------------------------------------------------------------------
    // navigation edit functions
    // -------------------------------------------------------------------------

    // loads the form associated with the navigation item into the
    // detail section for editing
    private editNavigation(navigationObservable: INavigationObservable): void {
        // show the form associated with the navigation item
        this.navigationID = navigationObservable.ID();
        this.templateFormID = navigationObservable.TemplateFormID();
        this.listID = null;
        this.hideAppointmentDetailContainer();
        this.hideLocationLogMapContainer();
        this.hideExceptionReportContainer();

        this.formDetail = true;
        this.appointmentDetail = false;

        this.loadFormItemsByTemplateFormID(navigationObservable.TemplateFormID(), this.appointmentID);

        this.showFormContainer();
        this.formControl.show();

        // update the UI
        this.unselectAllNavigation();
        navigationObservable.IsSelected(true);
    }

    // shows the report associated with the navigation item
    private showNavigationReport(navigationObservable: INavigationObservable): JQuery.Promise<any, any, any> {
        // load report data
        const me: TaskController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        const waitContainer: JQuery = UI.showWaitIndicator(me.container);

        this.hideFormContainer();
        this.hideAppointmentDetailContainer();
        this.hideLocationLogMapContainer();
        this.showExceptionReportContainer();

        this.appointmentDetail = false;
        this.formDetail = false;

        AppointmentData.GetExceptionReportData(me.appointmentID)
            .done((response: IResponse<AppointmentData.IAppointmentExceptionReport>) => {
                if (response.Success) {
                    // display report data
                    me.updateExceptionReport(response.Payload);

                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        // update the UI
        this.unselectAllNavigation();
        navigationObservable.IsSelected(true);

        return deferred.promise();
    }

    // -------------------------------------------------------------------------
    // list editing functions
    // -------------------------------------------------------------------------

    // loads the specified list item into the detail screen for editing
    private editList(listObservable: IListObservable): void {

        this.hideAppointmentDetailContainer();
        this.hideLocationLogMapContainer();
        this.hideExceptionReportContainer();
        this.showFormContainer();
        this.listID = listObservable.ID();
        this.appointmentDetail = false;
        this.formDetail = true;
        this.navigationID = listObservable.NavigationID();
        this.templateFormID = listObservable.TemplateFormID();
        this.loadFormItemsByTemplateFormID(listObservable.TemplateFormID(), this.appointmentID);
        // this.formControl.load(loadParams);
        this.formControl.show();

        // update the UI
        this.unselectAllNavigation();
        listObservable.IsSelected(true);
    }

    private moveListItem(listObservableToMove: IListObservable, moveUp: boolean): void {
        // a list item can be under a Navigation item, or under a List item
        // need to figure out which one, then do the move

        // check navigation items first
        const me: TaskController = this;
        let i: number = 1;
        let itemFound: boolean = false;

        this.viewModelTree.Navigations().forEach((navigation: INavigationObservable) => {
            if (itemFound) { return; }

            let index: number = navigation.Lists.indexOf(listObservableToMove);
            if (index >= 0) {
                // item is under this navigation item
                navigation.Lists.remove(listObservableToMove);
                if (moveUp) {
                    index = index - 1;

                } else {
                    index = index + 1;
                }

                navigation.Lists.splice(index, 0, listObservableToMove);
                navigation.Lists().forEach((list: IListObservable) => {
                    list.SortOrder(i);
                    i++;
                });

                // save new list order
                me.saveListOrder(navigation.Lists());

                listObservableToMove.ShowActions(false);
                itemFound = true;
            } else {
                // check any children
                navigation.Lists().forEach((list: IListObservable) => {
                    if (itemFound) { return; }
                    if (list.Children().length === 0) { return; }
                    itemFound = me.moveListItemForList(listObservableToMove, moveUp, list);
                });
            }
        });
    }

    private moveListItemForList(listObservableToMove: IListObservable, moveUp: boolean, parentList: IListObservable): boolean {
        const me: TaskController = this;
        let i: number = 1;
        let itemFound: boolean = false;

        let index: number = parentList.Children.indexOf(listObservableToMove);
        if (index >= 0) {
            // found the item, so move it
            parentList.Children.remove(listObservableToMove);
            if (moveUp) {
                index = index - 1;
            } else {
                index = index + 1;
            }
            parentList.Children.splice(index, 0, listObservableToMove);
            parentList.Children().forEach((list: IListObservable) => {
                list.SortOrder(i);
                i++;
            });

            // save new list order
            me.saveListOrder(parentList.Children());

            listObservableToMove.ShowActions(false);
            return true;
        }

        // check any children lists
        parentList.Children().forEach((child: IListObservable) => {
            if (itemFound) { return; }
            if (child.Children().length === 0) { return; }

            itemFound = me.moveListItemForList(listObservableToMove, moveUp, child);
        });

        return itemFound;
    }

    // -------------------------------------------------------------------------
    // add form functions
    // -------------------------------------------------------------------------
    private createNewForm(ID: number, listName: string, templateFormID: number, navigationID: number): JQueryPromise<IListObservable> {
        const me: TaskController = this;
        const deferred: JQuery.Deferred<IListObservable> = $.Deferred();

        // web service call to copy new list form to database
        AppointmentData.SaveNewListForm(listName, ID, me.appointmentID, navigationID)
            .done((response: IResponse<AppointmentData.IAppointmentList>) => {
                if (response.Success) {
                    me.listID = response.Payload.ID;
                    const appointmentList: AppointmentData.IAppointmentList = {
                        ID: response.Payload.ID,
                        ParentID: response.Payload.ParentID,
                        NavigationID: navigationID,
                        Children: [], // forms never have children
                        AttributeForm: [], // attribute forms currently not supported
                        TemplateFormID: templateFormID,
                        ListForm: me.getAppointmentFormItems(response.Payload.ListForm), // the items on this form
                        ListName: listName,
                        IsActive: true,
                        HasForm: true,
                        HasAttributes: false, // attribute forms currently not supported
                        SortOrder: 0, // this will be set by the web service
                        CanDelete: true, // user can delete stuff they have added
                        ListItems: [], // not used for forms
                    };
                    const listObservable: IListObservable = me.createListObservable(appointmentList, navigationID);
                    deferred.resolve(listObservable);

                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            });
        return deferred.promise();
    }

    private getAppointmentFormItems(FormItems: AppointmentData.IAppointmentFormItem[]): AppointmentData.IAppointmentFormItem[] {
        const formItems: AppointmentData.IAppointmentFormItem[] = [];
        const me: TaskController = this;

        FormItems.forEach((FormItem: AppointmentData.IAppointmentFormItem) => {
            const formItem: AppointmentData.IAppointmentFormItem = {
                ID: FormItem.ID,
                TemplateFormItemID: FormItem.ID,
                AppointmentID: me.appointmentID,
                FormAnswer: FormItem.FormAnswer, // answers, will be entered by the user
                Comments: [],  // comments, will be entered by the user
                Documents: [],
                AdditionalFormItems: [], // will be populated if the user opens the additional form
                ControlTypeID: FormItem.ControlTypeID,
                FormText: FormItem.FormText,
                SortOrder: FormItem.SortOrder,
                IsRequired: FormItem.IsRequired,
                IsReadOnly: FormItem.IsReadOnly,
                IsVisible: FormItem.IsVisible,
                Fields: FormItem.Fields,
                AppointmentControlTypeAttribute: FormItem.AppointmentControlTypeAttribute,
                AppointmentFormItemDocumentTypesAllowed: FormItem.AppointmentFormItemDocumentTypesAllowed,
                TemplatePhraseID: FormItem.TemplatePhraseID,
                AdditionalTemplateFormID: FormItem.AdditionalTemplateFormID,
                MultipleAdditionalForms: FormItem.MultipleAdditionalForms,
                Targets: FormItem.Targets,
            };
            formItems.push(formItem);
        });

        return formItems;
    }

    // -------------------------------------------------------------------------
    // functions for getting the data structure needed
    // to update the appointment on the server
    // -------------------------------------------------------------------------
    private isFormItemsValid(isAdditionalForm: boolean): boolean {
        const me: TaskController = this;
        // if is additional form then show error messages for additional form items
        if (isAdditionalForm) {
            // additional fromitem validation check
            if (!me.additionalFormControl.isValid()) {
                UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
                return false;
            }
        } else {
            // formitem validation check
            if (!me.formControl.isValid()) {
                UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
                return false;
            }

        }

        return true;
    }

    // -------------------------------------------------------------------------
    // uI functions
    // -------------------------------------------------------------------------

    // this function is called when the user is changing what is
    // displayed in the detail section (navigation or list) .
    // if the detail section has invalid values, will display an error
    // message and not let the user continue.
    // if the detail section has unsaved changes, will ask the
    // user if they want to continue
    private confirmDetailChange(onConfirmed: () => void): void {

        if ( this.detailsChanged()) {
            UI.showConfirmDialog("There are unsaved changes on this page.  "
            + "Are you sure you want to close this page and lose these unsaved changes?",
            () => { onConfirmed(); });
        }  else {
            onConfirmed();
        }
    }

    private detailsChanged(): boolean {
        const me: TaskController = this;
        const formItemsChanged: boolean = me.formControl.haveFieldsChanged();
        const taskDetails: string = ko.toJSON(this.viewModelAppointmentDetail);
        const taskfieldsChanged: boolean = (this.fieldsOnLoad !== taskDetails);

        if ((this.appointmentDetail && taskfieldsChanged) ||
            (this.formDetail && formItemsChanged)) {
                return true;
            }
        return false;
    }

    private unselectAllNavigation(): void {
        const me: TaskController = this;

        this.viewModelTree.Navigations().forEach((navigation: INavigationObservable) => {
            navigation.IsSelected(false);
            if (navigation.Lists().length > 0) {
                me.unselectAllLists(navigation.Lists);
            }
        });
    }

    private unselectAllLists(lists: KnockoutObservableArray<IListObservable>): void {
        const me: TaskController = this;

        lists().forEach((list: IListObservable) => {
            list.IsSelected(false);
            if (list.Children().length > 0) {
                me.unselectAllLists(list.Children);
            }
        });
    }

    private showFormContainer(): void {
        this.container.find("div[data-id='formContainer']").removeClass("hidden");
    }

    private hideFormContainer(): void {
        this.container.find("div[data-id='formContainer']").addClass("hidden");
    }

    private showAdditionalFormDialog(): void {
        this.container.find("div[data-id='additionalFormDialog']").modal("show");
    }

    private showAppointmentDetailContainer(): void {
        this.container.find("div[data-id='appointmentDetailContainer']").removeClass("hidden");
    }

    private hideAppointmentDetailContainer(): void {
        this.container.find("div[data-id='appointmentDetailContainer']").addClass("hidden");
    }

    private showLocationLogMapContainer(): void {
        this.container.find("div[data-id='locationLogMapContainer']").removeClass("hidden");
    }
    private hideLocationLogMapContainer(): void {
        this.container.find("div[data-id='locationLogMapContainer']").addClass("hidden");
    }

    private showExceptionReportContainer(): void {
        this.container.find("div[data-id='exceptionReportContainer']").removeClass("hidden");
    }

    private hideExceptionReportContainer(): void {
        this.container.find("div[data-id='exceptionReportContainer']").addClass("hidden");
    }

    // -------------------------------------------------------------------------
    // additional form functions
    // -------------------------------------------------------------------------
    private confirmedOpenAdditionalForm(templateFormID: number): void {
        const me: TaskController = this;
        // get form items for additional form and open additional form
        const myformitems = me.formControl.getFormItems();
        const origFormItemID: number = myformitems.FormItems[me.additionalviewModel.formItemIndex()].ID;

        me.loadAdditionalFormItemsByTemplateFormID(templateFormID, this.appointmentID, origFormItemID);
    }

    private openAdditionalForm(templateFormID: number): void {
        const me: TaskController = this;
        me.confirmedOpenAdditionalForm(templateFormID);
    }

    // -------------------------------------------------------------------------
    // save appointment detail functions
    // appointment detail are fields like appointment date, time,
    // assigned user, etc.
    // -------------------------------------------------------------------------
    private isAppointmentDetailValid(): boolean {
        let isValid: boolean = true;
        const appointment: IViewModelAppointmentDetail = this.viewModelAppointmentDetail;
        const appointmentDate: string = this.viewModelAppointmentDetail.AppointmentDate();
        const appointmentTime: string = this.viewModelAppointmentDetail.AppointmentTime();
        let apptDateTime: moment.Moment;

        this.viewModelAppointmentDetail.AppointmentDateTimeInvalid(false);

        const momentDateFormat: string = UI.getMomentDateFormat(appointment.DateFormat);
        const momentTimeFormat: string = UI.getMomentTimeFormat(appointment.TimeFormat);

        const momentFormat: string = momentDateFormat + " " + momentTimeFormat;
        if (!Util.isNullOrEmpty(appointmentDate) || !Util.isNullOrEmpty(appointmentTime)) {
            // either or date or time or both has been entered
            if (Util.isNullOrEmpty(appointmentDate) || Util.isNullOrEmpty(appointmentTime)) {
                // missing neither the date or time
                isValid = false;
                this.viewModelAppointmentDetail.AppointmentDateTimeInvalid(true);
            } else {
                apptDateTime = moment(this.viewModelAppointmentDetail.AppointmentDate() +
                    " " + this.viewModelAppointmentDetail.AppointmentTime(), momentFormat, true);
                if (!apptDateTime.isValid()) {
                    // date or time is not valid
                    isValid = false;
                    this.viewModelAppointmentDetail.AppointmentDateTimeInvalid(true);
                }
            }
        }
        // else, nothing entered, this is OK

        return isValid;
    }
    private saveListOrder(lists: IListObservable[]): JQueryPromise<any> {
        const me: TaskController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        let waitContainer: JQuery;

        waitContainer = UI.showWaitIndicator(me.container);
        AppointmentData.SaveListOrder(ko.toJS(lists))
            .done((response: IResponse<any>) => {
                if (response.Success) {
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

    private saveAppointmentDetail(): JQueryPromise<any> {
        const me: TaskController = this;
        const deferred: JQuery.Deferred<any> = $.Deferred();
        let waitContainer: JQuery;
        let apptDateTime: moment.Moment = moment();
        const appointment: IViewModelAppointmentDetail = this.viewModelAppointmentDetail;
        if (!this.isAppointmentDetailValid()) {
            deferred.reject();
            return deferred.promise();
        }

        const momentDateFormat: string = UI.getMomentDateFormat(appointment.DateFormat);
        const momentTimeFormat: string = UI.getMomentTimeFormat(appointment.TimeFormat);

        const momentFormat: string = momentDateFormat + " " + momentTimeFormat;

        if (appointment.AppointmentDate() && appointment.AppointmentTime()) {
            apptDateTime = moment(appointment.AppointmentDate()
                + " " + appointment.AppointmentTime(), momentFormat);
        }

        waitContainer = UI.showWaitIndicator(me.container);
        const saveAppointmentDetailRequest: AppointmentData.ISaveAppointmentDetailRequest = {
            appointmentID: this.appointmentID,
            appointmentDate: apptDateTime,
            statusID: this.viewModelAppointmentDetail.StatusID(),
            userID: this.viewModelAppointmentDetail.UserID(),
            languageID: this.viewModelAppointmentDetail.LanguageID(),
        };

        AppointmentData.SaveAppointmentDetail(saveAppointmentDetailRequest)
            .done((response: IResponse<AppointmentData.IAppointmentDetail>) => {
                if (response.Success) {
                    UI.showNotificationMessage(Constants.PHRASE_RECORDSAVED, UI.messageTypeValues.success);
                     // refresh the fieldsOnLoad with LatestSaved Appointment Details
                    this.fieldsOnLoad = ko.toJSON(this.viewModelAppointmentDetail);
                    deferred.resolve();
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                    deferred.reject();
                }
            })
            .fail(() => {
                deferred.reject();
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });

        return deferred.promise();
    }

}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/task.ts